
import React, { useEffect } from 'react';
import { BlogPost } from '../types';

interface Props {
  post: BlogPost;
  onNavigate: (page: string) => void;
}

export const PostDetail: React.FC<Props> = ({ post, onNavigate }) => {
  useEffect(() => {
    // Set document title for basic SEO simulation
    document.title = `${post.title} | Stealth Modular Houses`;
    return () => { document.title = 'Stealth Modular House'; };
  }, [post.title]);

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Header for Post */}
      <section className="relative h-[40vh] md:h-[60vh] overflow-hidden">
        <img 
          src={post.images[0]} 
          className="w-full h-full object-cover" 
          alt={post.title} 
        />
        <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" />
        <div className="absolute inset-0 flex items-center justify-center text-center p-6">
          <div className="max-w-4xl space-y-4">
            <div className="flex justify-center gap-2">
              {post.tags.map(tag => (
                <span key={tag} className="text-[10px] font-bold uppercase tracking-[0.2em] bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-white">#{tag}</span>
              ))}
            </div>
            <h1 className="text-3xl md:text-6xl font-light text-white uppercase tracking-tight leading-tight">{post.title}</h1>
            <p className="text-white/70 text-xs md:text-sm font-bold uppercase tracking-widest">{post.date}</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <article className="max-w-3xl mx-auto py-16 md:py-24 px-6">
        <div className="space-y-8">
          <button 
            onClick={() => onNavigate('blog')}
            className="flex items-center space-x-2 text-stone-400 hover:text-stone-900 transition-colors group mb-12"
          >
            <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-[10px] font-bold uppercase tracking-widest">Back to Magazine</span>
          </button>

          <div className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed space-y-6">
            {post.content.split('\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {/* Call to action for every post */}
          <div className="mt-20 p-10 md:p-16 bg-stone-900 rounded-[3rem] text-white text-center space-y-8">
            <h3 className="text-2xl md:text-4xl font-light uppercase tracking-tight">Interested in building your own?</h3>
            <p className="text-stone-400 max-w-xl mx-auto">Learn more about the Stealth modular system and how it can transform your business or lifestyle.</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button onClick={() => onNavigate('contacts')} className="bg-white text-stone-900 px-8 py-4 rounded-full font-bold uppercase text-[10px] tracking-widest hover:bg-stone-100 transition-all">Contact Us</button>
              <button onClick={() => onNavigate('investment')} className="border-2 border-white/20 px-8 py-4 rounded-full font-bold uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all">ROI Calculator</button>
            </div>
          </div>
        </div>
      </article>

      {/* Suggested Articles */}
      <section className="bg-stone-50 py-24 px-6 border-t border-stone-200">
        <div className="max-w-7xl mx-auto space-y-12">
          <h2 className="text-2xl md:text-3xl font-light uppercase tracking-widest text-center">You might also like</h2>
          {/* We'll show a quick grid here as a filler or simple "Explore more" link */}
          <div className="text-center">
             <button onClick={() => onNavigate('blog')} className="text-[10px] font-black uppercase tracking-[0.3em] border-b-2 border-stone-900 pb-2">View All Articles</button>
          </div>
        </div>
      </section>
    </div>
  );
};
